import type { Profile, PresetProfile, AnyProfile } from '@/types'
import { credentialsManager } from './credentials-manager'
import { getExecutionMode, getSelectedRegion } from './probe-strategy'
import { useAppStore } from '@/store/app-store'
import { isFullArn, extractAccountFromArn } from '@/utils/arn-utils'

export interface PreflightResult {
  profileId: string
  profileName: string
  ok: boolean
  error?: string
}

/**
 * Runs a lightweight pre-flight check for each enabled profile before
 * the full benchmark starts. Catches credential, model access, and
 * guardrail issues early so the user gets fast, clear feedback.
 *
 * In local mode: validates credentials exist, then does a minimal
 * InvokeModel call (1 token) to verify model access and guardrail config.
 *
 * In cloud mode: sends a single-iteration probe through the backend.
 */
export async function runPreflight(profiles: AnyProfile[]): Promise<PreflightResult[]> {
  const mode = getExecutionMode()
  const results: PreflightResult[] = []

  if (mode === 'cloud') {
    // Cloud mode - backend handles credentials, skip preflight
    return profiles.map(p => ({ profileId: p.id, profileName: p.name, ok: true }))
  }

  // Local mode - check credentials first
  const creds = await credentialsManager.getAwsCredentials()
  if (!creds) {
    return profiles.map(p => ({
      profileId: p.id,
      profileName: p.name,
      ok: false,
      error: 'No AWS credentials. Go to Settings → AWS Credentials.',
    }))
  }

  // De-duplicate by modelId+guardrailId to avoid redundant checks
  const checked = new Map<string, PreflightResult>()

  for (const profile of profiles) {
    const isPreset = 'isPreset' in profile && profile.isPreset
    const preset = profile as PresetProfile
    const custom = profile as Profile

    const modelId = isPreset ? (preset.modelId || preset.presetName) : custom.modelId
    const region = isPreset
      ? (preset.region || getSelectedRegion())
      : (custom.region || getSelectedRegion())
    const guardrailId = isPreset ? preset.guardrailId : custom.guardrailId
    const guardrailVersion = isPreset ? preset.guardrailVersion : custom.guardrailVersion
    const profileType = isPreset
      ? (preset.profileType?.toLowerCase().includes('direct') ? 'anthropic-direct'
        : preset.profileType?.toLowerCase().includes('grok') ? 'grok'
        : preset.guardrailId ? 'bedrock-guardrail' : 'bedrock')
      : custom.type

    // Skip non-bedrock profiles (anthropic-direct, grok) - they have their own key checks
    if (profileType === 'anthropic-direct' || profileType === 'grok') {
      results.push({ profileId: profile.id, profileName: profile.name, ok: true })
      continue
    }

    // Skip guardrail presets with no guardrail configured - require user to set one
    if (isPreset && preset.profileType?.toLowerCase().includes('guardrail') && !guardrailId) {
      results.push({ profileId: profile.id, profileName: profile.name, ok: false, error: 'Guardrail ID is required. Edit this profile and enter a guardrail ID from your AWS account.' })
      continue
    }

    const cacheKey = `${modelId}|${region}|${guardrailId || ''}`
    if (checked.has(cacheKey)) {
      const prev = checked.get(cacheKey)!
      results.push({ profileId: profile.id, profileName: profile.name, ok: prev.ok, error: prev.error })
      continue
    }

    try {
      const { BedrockRuntimeClient, InvokeModelCommand } = await import('@aws-sdk/client-bedrock-runtime')

      const client = new BedrockRuntimeClient({
        region,
        credentials: {
          accessKeyId: creds.accessKeyId,
          secretAccessKey: creds.secretAccessKey,
          ...(creds.sessionToken ? { sessionToken: creds.sessionToken } : {}),
        },
      })

      const body = JSON.stringify({
        anthropic_version: 'bedrock-2023-05-31',
        max_tokens: 1,
        messages: [{ role: 'user', content: 'hi' }],
      })

      const input: Record<string, unknown> = {
        modelId,
        contentType: 'application/json',
        accept: 'application/json',
        body: new TextEncoder().encode(body),
      }

      if (guardrailId) {
        input.guardrailIdentifier = guardrailId
        input.guardrailVersion = guardrailVersion || 'DRAFT'
      }

      await client.send(new InvokeModelCommand(input as any))

      const r: PreflightResult = { profileId: profile.id, profileName: profile.name, ok: true }
      checked.set(cacheKey, r)
      results.push(r)
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err)
      const name = (err as { name?: string })?.name || ''

      let error: string
      if (name === 'AccessDeniedException' || message.includes('Access Denied')) {
        const userAccountId = useAppStore.getState().awsAccountId
        const accountInfo = userAccountId ? ` (your account: ${userAccountId})` : ''
        const displayModelId = isFullArn(modelId) ? profile.name : modelId

        if (isFullArn(modelId)) {
          const arnAccount = extractAccountFromArn(modelId)
          if (arnAccount && userAccountId && arnAccount !== userAccountId) {
            error = `Access denied for "${displayModelId}"${accountInfo}. The model ID is an ARN from account ${arnAccount}, but you're authenticated as account ${userAccountId}. Application inference profile ARNs are account-specific - edit this profile and use the short model ID (e.g., us.anthropic.claude-opus-4-6-v1) instead.`
          } else {
            error = `Access denied for "${displayModelId}" in ${region}${accountInfo}. The model ID is a full ARN which is account-specific. Try using the short model ID instead.`
          }
        } else {
          error = `Access denied for "${displayModelId}" in ${region}${accountInfo}. Check your IAM policy allows bedrock:InvokeModel. For Anthropic models, first-time users may need to submit use case details in the Bedrock console.`
        }
      } else if (name === 'ValidationException' || message.includes('model identifier is invalid')) {
        const displayModelId = isFullArn(modelId) ? profile.name : modelId
        const userAccountId = useAppStore.getState().awsAccountId
        if (isFullArn(modelId)) {
          const arnAccount = extractAccountFromArn(modelId)
          error = `Invalid model ID for "${displayModelId}". The ARN belongs to account ${arnAccount || 'unknown'} and doesn't exist in your account${userAccountId ? ` (${userAccountId})` : ''}. Edit this profile and use the short model ID (e.g., us.anthropic.claude-opus-4-6-v1) instead.`
        } else {
          error = `Invalid model ID: ${modelId}. Check the model ID is correct for ${region}.`
        }
      } else if (message.includes('guardrail') || message.includes('does not exist')) {
        error = `Guardrail ${guardrailId} not found in ${region}. Remove or update the guardrail config.`
      } else if (name === 'ResourceNotFoundException' || message.includes('Could not resolve')) {
        const displayModelId = isFullArn(modelId) ? profile.name : modelId
        if (isFullArn(modelId)) {
          error = `Model "${displayModelId}" not found. The ARN is account-specific - edit this profile and use the short model ID instead.`
        } else {
          error = `Model "${displayModelId}" not available in ${region}.`
        }
      } else if (name === 'ExpiredTokenException' || message.includes('expired')) {
        error = 'AWS credentials expired. Re-authenticate in Settings.'
      } else if (message.includes('UnrecognizedClientException') || message.includes('security token')) {
        error = 'Invalid AWS credentials. Check your access key and secret.'
      } else {
        error = message.length > 120 ? message.slice(0, 120) + '…' : message
      }

      const r: PreflightResult = { profileId: profile.id, profileName: profile.name, ok: false, error }
      checked.set(cacheKey, r)
      results.push(r)
    }
  }

  return results
}
